
import React, { useEffect, useState } from 'react';
import { TrashIcon } from '@common_components/icons/trash.icon';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import { useTranslation } from 'react-i18next';
import Button from '@common_components/buttons/button.component';
import { Engagement } from 'interfaces/engagement.interface';
import { useDispatch, useSelector } from 'react-redux';
import { ArchiveBoxIcon, CheckIcon, CommonGlobalService, dayjs, GroupedOption, MultiSelect, Option, RootState, usePersistantState, UUID } from '@umanforge/common';
import { EventManagerService } from 'services/eventManagers.service';
import { User } from 'interfaces/user.interface';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import { VisualisationService } from 'services/visualisation.service';
import { KEY } from 'constants/local_storage_key.constant';

interface Props {
  isArchived?: boolean;
  engagements: Engagement[];
  eventManagerOptions: GroupedOption<{ id: UUID, name: string }>[];
  isViewMode: boolean | undefined;
  updateEngagement: ( index: number, key: keyof Engagement, value: string[] | string | Date ) => void;
  removeEngagement: ( index: number ) => void;
  isAdminOfEventManager?: boolean;
}

const EngagementList: React.FC<Props> = ( { engagements, isArchived, eventManagerOptions, isViewMode, updateEngagement, removeEngagement, isAdminOfEventManager } ) => {
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ selectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const isAdmin =  isAdminOfEventManager || ( me != undefined && selectedSectorId != undefined && CommonGlobalService.isAdminOfSector( me, selectedSectorId ) );

  return (
    <>
      {
        engagements.map( ( engagement, index ) => {
          let isLineDisabled = !!isArchived || !!engagement.archived;
          if ( !isLineDisabled && !isAdmin && me && me.id ) {
            const isEmpty = engagement.participants.length === 0;
            const haveCurrentUser = engagement.participants.includes( me.id );
            const haveAloneBacklog = engagement.participants.includes( DEFAULT_BACKLOG.ID ) && engagement.participants.length === 1;
            isLineDisabled = !( isEmpty || haveCurrentUser || haveAloneBacklog );
          }

          const update = ( key: keyof Engagement, value: string[] | string | Date ) => {
            updateEngagement( index, key, value );
          };

          const remove = (): void => {
            removeEngagement( index );
          };

          return (
            <EngagementLine
              key={ index }
              engagement={ engagement }
              eventManagerOptions={ eventManagerOptions }
              hideBin={ engagements.length <= 1 }
              isLineDisabled={ isLineDisabled }
              isViewMode={ isViewMode }
              isAdmin={ isAdmin ?? false }
              updateEngagement={ update }
              removeEngagement={ remove }
            />
          );

        } )
      }
    </>
  );
};

interface EngagementLineProps {
  engagement: Engagement;
  eventManagerOptions: GroupedOption<{ id: UUID, name: string }>[];
  isLineDisabled: boolean;
  isAdmin: boolean;
  hideBin: boolean;
  isViewMode: boolean | undefined;
  updateEngagement: ( key: keyof Engagement, value: string[] | string | Date ) => void;
  removeEngagement: () => void;
}

const EngagementLine: React.FC<EngagementLineProps> = ( { engagement, eventManagerOptions, isAdmin, hideBin, isLineDisabled, isViewMode, updateEngagement, removeEngagement } ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const currentUserId = useSelector( ( state: RootState ) => {return state.user?.value?.id;} );

  const canDeleteEngagement = (
    isAdmin ||
    !engagement.participants.some( ( id: string ) => { return id != currentUserId && id != DEFAULT_BACKLOG.ID; } )
  );

  const [ selectedEventManagerId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );

  const [ selectedEventManagers, setSelectedEventManagers ] = useState<Option<{ id: UUID, name: string }>[]>( [] );

  const [ selectedParticipants, setSelectedParticipants ] = useState<Option<User>[]>( [] );
  const [ participantsOption, setParticipantsOption ] = useState<Option<User>[]>( [] );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  useEffect( () => {
    if ( selectedEventManagerId && isFirstRender ) {
      init();
    }
  }, [ selectedEventManagerId ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      init();
    }
  }, [ engagement ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedEventManagers( getSelectedEventManagers( eventManagerOptions, selectedEventManagers.map( eventManager => { return eventManager.object.id; } ) ) );
    }
  }, [ eventManagerOptions ] );
  useEffect( () => {
    if ( !isFirstRender ) {
      getParticpantsOption( selectedEventManagers, [] )
        .then( setParticipantsOption );
    }
  }, [ selectedEventManagers ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedParticipants( getSelectedParticpants( participantsOption, selectedParticipants.map( participant => { return participant.object.id; } ) ) );
    }
  }, [ participantsOption ] );

  const init = async () => {
    const updatedSelectedEventManagers = getSelectedEventManagers( eventManagerOptions, engagement.eventManagerIds );
    setSelectedEventManagers( updatedSelectedEventManagers );

    const updatedParticipantsOption = await getParticpantsOption( updatedSelectedEventManagers, engagement.participants );
    setParticipantsOption( updatedParticipantsOption );

    const updatedSelectedParticipants = getSelectedParticpants( updatedParticipantsOption, engagement.participants );
    setSelectedParticipants( updatedSelectedParticipants );
    setIsFirstRender( false );
  };

  const getSelectedEventManagers = ( allEventManagers: GroupedOption<{ id: UUID, name: string }>[], eventManagersId: UUID[] ): Option<{ id: UUID, name: string }>[] => {
    return allEventManagers
      .map( eventManager => { return eventManager.options; } )
      .flat()
      .filter( eventManagerOption => { return eventManagersId.includes( eventManagerOption.object.id ); } );
  };

  const getParticpantsOption = async ( eventManagers: Option<{ id: UUID, name: string }>[], participantsId: UUID[] ): Promise<Option<User>[]> => {
    const updatedParticipantsOption: Option<User>[] = [];
    if ( eventManagers.length > 0 ) {

      const usersOfEventManagers = [];
      if ( isViewMode ) {
        usersOfEventManagers.push( ...( await VisualisationService.getCommonUsersByEventManager( [ selectedEventManagerId ?? '' ], visualisationToken, dispatch ) ) );
      } else {
        const eventManagersId = eventManagers.map( eventManager => { return eventManager.object.id; } );
        usersOfEventManagers.push( ...( await EventManagerService.getUsersByEventManagerIds( eventManagersId, token, dispatch ) ) );
      }

      const repsonseUsers: User[] = usersOfEventManagers
        .map( user => { return { ...user, fullname: user.firstname + ' ' + user.lastname }; } )
        .filter( ( participant, index, self ) => { return index === self.findIndex( participantBis => { return participantBis.id === participant.id; } ); } );
      if ( !repsonseUsers.some( user => { return user.id === DEFAULT_BACKLOG.ID; } ) ) {
        repsonseUsers.push( { id: DEFAULT_BACKLOG.ID, firstname: t( DEFAULT_BACKLOG.NAME ), lastname: '', fullname: t( DEFAULT_BACKLOG.NAME ) } );
      } else {
        repsonseUsers.forEach( user => {
          if ( user.id === DEFAULT_BACKLOG.ID ) {
            user.firstname = user.firstname === DEFAULT_BACKLOG.NAME ? t( user.firstname ) : user.firstname;
            user.fullname = user.firstname === DEFAULT_BACKLOG.NAME ? t( user.firstname ) : user.firstname;
          }
        } );
      }
      participantsId
        .filter( participantOfEngId => {
          return !repsonseUsers.some( participant => { return participant.id === participantOfEngId; } );
        } )
        .forEach( participantId => {
          repsonseUsers.push( { id: participantId, firstname: t( 'translation:participant_not_found' ), lastname: '', fullname: t( 'translation:participant_not_found' ) } );
        } );

      const updatedParticipants = CommonGlobalService.toOptions( repsonseUsers );
      updatedParticipants.forEach( user => {
        user.isDisabled = !( isAdmin || isLineDisabled || ( user.object.id === currentUserId || user.object.id === DEFAULT_BACKLOG.ID ) );
      } );
      updatedParticipantsOption.push( ...updatedParticipants );
    }
    return updatedParticipantsOption;
  };

  const getSelectedParticpants = ( allParticipant: Option<User>[], participantsId: UUID[] ): Option<User>[] => {
    return allParticipant
      .filter( participantOption => { return participantsId.includes( participantOption.object.id ); } );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_64px] gap-x-5 gap-y-3 py-2 px-2 w-full items-center relative">
      <div className="relative">
        <div className="absolute -top-5 -left-1 z-10 pointer-events-none">
          <div className='flex gap-1'>
            { engagement.archived ? <ArchiveBoxIcon className='h-7 w-7 p-0.5 bg-purple-300 text-purple-700 border-2 border-purple-700 shadow-lg rounded-full' /> : null }
            { engagement.completed ? <CheckIcon className="h-7 w-7 p-0.5 bg-green-300 text-green-700 border-2 border-green-700 shadow-lg rounded-full" /> : null }
          </div>
        </div>
        <label className="block lg:hidden text-sm text-gray-500 mb-1">{t( 'translation:event_manager' )}</label>
        <MultiSelect
          getValues={ eventManagerOptions }
          selectedValues={ selectedEventManagers }
          onChange={ updatedEventManagers => {
            setSelectedEventManagers( updatedEventManagers );
            updateEngagement( 'eventManagerIds', updatedEventManagers.map( eventManager => { return eventManager.object.id; } ) );
          } }
          disabled={ isLineDisabled }
          clearable={ false }
          required
        />
      </div>
      <div>
        <label className="block lg:hidden text-sm text-gray-500 mb-1">{t( 'common:participant' )}</label>
        <MultiSelect
          getValues={ participantsOption }
          selectedValues={ selectedParticipants }
          onChange={ updatedParticipants => {
            setSelectedParticipants( updatedParticipants );
            updateEngagement( 'participants', updatedParticipants.map( participant => { return participant.object.id; } ) );
          } }
          disabled={ isLineDisabled }
          clearable={ false }
        />
      </div>
      <div>
        <label className="block lg:hidden text-sm text-gray-500 mb-1">{t( 'translation:date_of_engagement' )}</label>
        <DatePickerSoloInput
          width="w-full lg:w-36 lg:ml-5"
          minDate={ dayjs().toDate() }
          defaultValue={ dayjs( engagement.deliveryDate ).toDate() }
          onChange={ e => {
            if ( e ) {
              return updateEngagement( 'deliveryDate', e );
            }
          } }
          disable={ isLineDisabled }
        />
      </div>
      <div className="flex justify-center">
        {
          !isLineDisabled && !hideBin && canDeleteEngagement ? (
            <Button
              icon={<TrashIcon size={5} />}
              roundedRadius="rounded-xl"
              padding="p-3"
              textColor="text-red-700"
              textColorHover="hover:text-red-700"
              hoverBorderColor="hover:border-transparent"
              color="bg-red-100"
              shadow="hover:shadow-md"
              colorHover="hover:bg-red-200"
              onClick={ () => {return removeEngagement(); } }
            />
          ) : (
            <div className="w-12 h-12" />
          )
        }
      </div>
    </div>
  );
};

export default EngagementList;
